

'use client';

import Link from 'next/link';
import { Menu, Sparkles, User, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose, DialogDescription } from '@/components/ui/dialog';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { mainNavLinks } from '@/lib/nav-data';
import { useAuth, type Role } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { CreateProfileDialog } from '@/components/create-profile-dialog';
import { AuthDialog } from '@/components/auth-dialog';
import { MainMenu } from '@/components/header/main-menu';
import { LoggedInContent } from '@/components/header/loggedin-content';
import { LoggedOutContent } from '@/components/header/loggedout-content';
import { NavLink } from '@/components/header/nav-link';
import { MobileSecondaryHeader } from './mobile-secondary-header';
import { NameAvatar } from './ui/name-avatar';
import { Card } from '@/components/ui/card';


export const Logo = ({ className }: { className?: string }) => (
  <Image src="/img/HJPNG.png" alt="HelloJob Logo" width={120} height={40} className={cn("h-10 w-auto", className)} priority />
);

export function Header() {
  const { role, isLoggedIn, user } = useAuth();
  const [isClient, setIsClient] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNav, setShowNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const isMobile = useIsMobile();


  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || !isMobile) {
      setShowNav(true);
      return;
    }

    const controlNavbar = () => {
      if (window.scrollY > lastScrollY && window.scrollY > 80) { // if scroll down hide the navbar
        setShowNav(false);
      } else { // if scroll up show the navbar
        setShowNav(true);
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener('scroll', controlNavbar);

    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [isClient, isMobile, lastScrollY]);

  const isEditing = role === 'candidate'
  const createProfileButtonTextMobile = isEditing ? 'Sửa' : 'Tạo';

  return (
    <>
      <div className={cn(
        "sticky top-0 z-50 w-full transition-transform duration-300",
        isClient && !showNav && isMobile && "-translate-y-full"
      )}>
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
            <Link href="/" className="flex items-center gap-2">
              <Logo />
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm">
              {mainNavLinks.map((link) => (
                <NavLink
                  key={link.href}
                  href={link.href}
                  label={link.label}
                  icon={link.href === '/tao-ho-so-ai' ? Sparkles : undefined}
                />
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-2">
              {isClient && (
                <>

                  <MainMenu />
                </>
              )}
            </div>
            {isClient && isMobile && (
              <div className="flex items-center gap-2">

                {!isLoggedIn && (
                  <Button size="sm" onClick={() => setIsAuthDialogOpen(true)}>Đăng nhập</Button>
                )}

                {isLoggedIn && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="mr-1 rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 outline-none">
                        <NameAvatar src={user?.avatarUrl} fullName={user?.personalInfo?.fullName} size={32} />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-xl md:text-2xl font-headline text-center">Tài khoản HelloJob</DialogTitle>
                            <DialogDescription className="text-center text-sm md:text-base">
                                Quản lý thông tin và kết quả của bạn
                            </DialogDescription>
                        </DialogHeader>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 pt-4">
                            <DialogClose asChild>
                                <Link href="/ho-so-cua-toi" className="block h-full">
                                    <Card className="text-center p-4 md:p-6 hover:shadow-lg hover:border-[#0D8DC8] border-2 transition-all duration-300 cursor-pointer h-full flex flex-col items-center justify-center group bg-card">
                                        <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-[#0D8DC8]/10 flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                                            <User className="h-6 w-6 md:h-8 md:w-8 text-[#0D8DC8]" />
                                        </div>
                                        <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2 text-foreground">Xem hồ sơ</h3>
                                        <p className="text-muted-foreground text-xs md:text-sm">Xem thông tin cá nhân của bạn</p>
                                    </Card>
                                </Link>
                            </DialogClose>

                            <DialogClose asChild>
                                <Link href="/ho-so-cua-toi?mode=edit" className="block h-full">
                                    <Card className="text-center p-4 md:p-6 hover:shadow-lg hover:border-[#F2B92A] border-2 transition-all duration-300 cursor-pointer h-full flex flex-col items-center justify-center group bg-card">
                                        <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-[#F2B92A]/10 flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                                            <Edit className="h-6 w-6 md:h-8 md:w-8 text-[#F2B92A]" />
                                        </div>
                                        <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2 text-foreground">Sửa hồ sơ</h3>
                                        <p className="text-muted-foreground text-xs md:text-sm">Cập nhật thông tin hồ sơ của bạn</p>
                                    </Card>
                                </Link>
                            </DialogClose>

                            <DialogClose asChild>
                                <Link href="/tao-ho-so/tao-noi-dung-phong-van/ket-qua" className="block h-full">
                                    <Card className="text-center p-4 md:p-6 hover:shadow-lg hover:border-[#AFC536] border-2 transition-all duration-300 cursor-pointer h-full flex flex-col items-center justify-center group bg-card">
                                        <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-[#AFC536]/10 flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                                            <Sparkles className="h-6 w-6 md:h-8 md:w-8 text-[#AFC536]" />
                                        </div>
                                        <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2 text-foreground">Kết quả luyện phỏng vấn</h3>
                                        <p className="text-muted-foreground text-xs md:text-sm">Xem kết quả đánh giá từ AI</p>
                                    </Card>
                                </Link>
                            </DialogClose>
                        </div>
                    </DialogContent>
                  </Dialog>
                )}

                <CreateProfileDialog>
                  <Button className="bg-accent-orange hover:bg-accent-orange/90 text-white" size="sm">
                    {createProfileButtonTextMobile}
                  </Button>
                </CreateProfileDialog>

                <Button asChild variant="default" size="sm">
                  <Link href="/viec-lam-cua-toi">Việc</Link>
                </Button>
                <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Menu />
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="flex flex-col p-0">
                    <SheetHeader className="p-4 border-b">
                      <SheetTitle><Logo /></SheetTitle>
                    </SheetHeader>
                    {isLoggedIn ? <LoggedInContent /> : <LoggedOutContent setIsAuthDialogOpen={setIsAuthDialogOpen} />}
                  </SheetContent>
                </Sheet>
              </div>
            )}
          </div>
        </header>
        {isClient && isMobile && <MobileSecondaryHeader />}
      </div>
      <AuthDialog isOpen={isAuthDialogOpen} onOpenChange={(open: any) => {
        setIsAuthDialogOpen(open);
      }} />
    </>
  );
}
